import React, { useState } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import { Icon } from "../../../components/common/Icon";
import { FaUndo, FaCheck } from "react-icons/fa";
import DateRangePicker from "../../../components/common/DateRangePicker";
import { useTranslation } from "react-i18next";

// Chatbot selection removed; this filter is scoped to a single chatbot

export interface MessagesFilterValues {
  session_id?: string;
  start_date?: string;
  end_date?: string;
  text_search?: string;
  role?: "user" | "assistant" | "";
}

interface AIChatbotMessagesFilterProps {
  onFilterChange: (filters: MessagesFilterValues) => void;
  onReset?: () => void;
  initialValues?: MessagesFilterValues;
  className?: string;
  loading?: boolean;
}

const AIChatbotMessagesFilter: React.FC<AIChatbotMessagesFilterProps> = ({ onFilterChange, onReset, initialValues, className, loading }) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState({
    session_id: initialValues?.session_id || "",
    start_date: initialValues?.start_date || "",
    end_date: initialValues?.end_date || "",
    text_search: initialValues?.text_search || "",
  });
  const [role, setRole] = useState<"user" | "assistant" | "">((initialValues?.role as any) || "");

  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: MessagesFilterValues = {
      session_id: localFilters.session_id || undefined,
      start_date: localFilters.start_date || undefined,
      end_date: localFilters.end_date || undefined,
      text_search: localFilters.text_search || undefined,
      role: role || undefined,
    };
    onFilterChange(payload);
  };

  const handleReset = () => {
    const cleared = {
      session_id: "",
      start_date: "",
      end_date: "",
      text_search: "",
    };
    setLocalFilters(cleared);
    setRole("");
    onFilterChange({});
    onReset?.();
  };

  const toggleRole = (target: "user" | "assistant") => {
    setRole(prev => (prev === target ? "" : target));
  };

  return (
    <Form onSubmit={handleSubmit} className={`${className || ""} messages-filter-modern`}>
      <div className="filter-card-body">
        <div className="filter-search">
          <Form.Group className="flex-grow-1">
            <Form.Control
              type="text"
              name="text_search"
              placeholder={t("search_in_messages") || "Search messages..."}
              value={localFilters.text_search}
              onChange={handleInputChange}
              className="filter-input"
              aria-label={t("search_in_messages") || "Search in messages"}
            />
          </Form.Group>
        </div>
        <div className="filter-right">
          <div className="filter-range">
            <div className="date-range-inline">
              <DateRangePicker
                startDate={localFilters.start_date || undefined}
                endDate={localFilters.end_date || undefined}
                onChange={({ startDate, endDate }) =>
                  setLocalFilters(prev => ({
                    ...prev,
                    start_date: startDate,
                    end_date: endDate,
                  }))
                }
                className="filter-range-btn"
                menuClassName="p-3"
                toggleVariant="outline-secondary"
                placeholder={t("select_date_range") || "Date range"}
              />
            </div>
          </div>
          <div className="filter-role">
            <div className="role-chips" role="group" aria-label={t("role") || "Role"}>
              <button
                type="button"
                className={`chip ${role === "user" ? "active" : ""}`}
                onClick={() => toggleRole("user")}
                aria-pressed={role === "user"}
              >
                User
              </button>
              <button
                type="button"
                className={`chip ${role === "assistant" ? "active" : ""}`}
                onClick={() => toggleRole("assistant")}
                aria-pressed={role === "assistant"}
              >
                Assistant
              </button>
            </div>
          </div>
          <div className="filter-session">
            <Form.Group>
              <Form.Control
                type="text"
                name="session_id"
                placeholder={t("Session ID") || "Session ID"}
                value={localFilters.session_id}
                onChange={handleInputChange}
                className="filter-input"
                aria-label={t("Session ID") || "Session ID"}
              />
            </Form.Group>
          </div>
        </div>
      </div>
      <div className="filter-bottom">
        <Button type="button" onClick={handleReset} className="filter-btn-reset" disabled={!!loading} aria-label={t("reset_filters") || "Reset"}>
          Reset Filter
        </Button>
        <Button type="submit" className="filter-btn-apply" disabled={!!loading} aria-label={t("apply_filters") || "Apply"}>
          Apply
        </Button>
      </div>
      <style>{`
        .messages-filter-modern {
          width: 100%;
        }

        .filter-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        /* Search Row */
        .messages-filter-modern .filter-row {
          display: flex;
          gap: 12px;
          align-items: flex-end;
          flex-wrap: wrap;
        }

        .messages-filter-modern .filter-row:first-child {
          margin-bottom: 4px;
        }

        /* Control Row */
        .messages-filter-modern .filter-controls {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 12px;
        }

        /* Filter Groups */
        .messages-filter-modern .filter-group {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .messages-filter-modern .filter-group.filter-actions {
          display: flex;
          flex-direction: row;
          gap: 8px;
          justify-content: flex-end;
          align-items: flex-end;
          min-width: auto;
          grid-column: auto;
        }

        /* Input Styling */
        .messages-filter-modern .filter-input {
          height: 40px;
          padding: 8px 12px;
          border: 1px solid var(--border-color, #e0e0e0);
          border-radius: 6px;
          font-size: 14px;
          transition: all 0.2s ease;
          background: var(--bg-main, #fff);
          color: var(--text-primary, #333);
        }

        .messages-filter-modern .filter-input:focus {
          border-color: var(--brand-purple, #6366f1);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          outline: none;
        }

        .messages-filter-modern .filter-input::placeholder {
          color: var(--text-secondary, #999);
        }

        /* Date Range */
        .messages-filter-modern .date-range-inline {
          width: 100%;
        }

        .messages-filter-modern .date-range-inline .form-control {
          height: 40px;
          border-radius: 6px;
        }

        /* Role Chips */
        .messages-filter-modern .role-chips {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .messages-filter-modern .chip {
          appearance: none;
          background: var(--bg-secondary, #f5f5f5);
          color: var(--text-primary, #333);
          border: 1px solid var(--border-color, #e0e0e0);
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .messages-filter-modern .chip:hover {
          border-color: var(--brand-purple, #6366f1);
          background: rgba(99, 102, 241, 0.05);
        }

        .messages-filter-modern .chip.active {
          background: var(--brand-purple, #6366f1);
          color: white;
          border-color: var(--brand-purple, #6366f1);
        }

        .messages-filter-modern .chip.active:hover {
          opacity: 0.9;
        }

        /* Buttons */
        .messages-filter-modern .filter-btn {
          height: 40px;
          width: 40px;
          padding: 0;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--border-color, #e0e0e0);
          transition: all 0.2s ease;
          font-size: 16px;
        }

        .messages-filter-modern .filter-btn.btn-primary {
          background: var(--brand-purple, #6366f1);
          border-color: var(--brand-purple, #6366f1);
          color: white;
        }

        .messages-filter-modern .filter-btn.btn-primary:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .messages-filter-modern .filter-btn.btn-outline-secondary {
          background: var(--bg-secondary, #f5f5f5);
          color: var(--text-secondary, #666);
        }

        .messages-filter-modern .filter-btn.btn-outline-secondary:hover:not(:disabled) {
          border-color: var(--text-secondary, #666);
          background: var(--bg-main, #fff);
        }

        .messages-filter-modern .filter-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .messages-filter-modern .filter-controls {
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .messages-filter-modern .filter-controls {
            grid-template-columns: 1fr 1fr;
          }

          .messages-filter-modern .filter-group.filter-actions {
            grid-column: 1 / -1;
            justify-content: flex-start;
          }
        }

        @media (max-width: 576px) {
          .messages-filter-modern .filter-controls {
            grid-template-columns: 1fr;
          }

          .messages-filter-modern .filter-group.filter-actions {
            grid-column: auto;
          }

          .messages-filter-modern .role-chips {
            width: 100%;
          }

          .messages-filter-modern .chip {
            flex: 1;
            min-width: 70px;
          }
        }
      `}</style>
    </Form>
  );
};

export default AIChatbotMessagesFilter;
